import type { RouteType } from '../../types/route-type';

/**
 * How a route's type is drawn, as plain data on a 24-unit box with a stroke of 2:
 * the screen and the printed sheet draw these same shapes with their own elements
 * (`RouteTypeIcon`, `crag-sheet-pdf`), so the two cannot come to disagree.
 *
 * `rock` is filled with a tint under its stroke, `solid` is filled with no stroke.
 * Traverse, endurance and crux are Lucide's move-horizontal, infinity and zap (ISC).
 */
export interface RouteTypeGlyphPath {
  d: string;
  fill?: 'rock' | 'solid';
}

/** How far the tint of the rock shows. */
export const ROCK_TINT = 0.25;

/** The rock seen from the front, with what there is to hold on it. */
const ROCK_FACE = { d: 'M7 3h10a4 4 0 0 1 4 4v10a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V7a4 4 0 0 1 4-4z', fill: 'rock' } as const;

function hole(cx: number, cy: number, rx: number, ry: number): RouteTypeGlyphPath {
  return {
    d: `M${cx - rx} ${cy}a${rx} ${ry} 0 1 0 ${rx * 2} 0a${rx} ${ry} 0 1 0 ${-rx * 2} 0z`,
    fill: 'solid',
  };
}

export const ROUTE_TYPE_GLYPHS: Record<RouteType, RouteTypeGlyphPath[]> = {
  // The wall seen from the side, rock on the left: its face is the climbing's angle.
  low_angle: [{ d: 'M3 21h18L3 8z', fill: 'rock' }],
  slab: [{ d: 'M3 21h13L9 3H3z', fill: 'rock' }],
  overhanging_face: [{ d: 'M3 21h6l6-18H3z', fill: 'rock' }],
  overhang: [{ d: 'M3 21h3L20 7V3H3z', fill: 'rock' }],
  roof: [{ d: 'M3 21h6V10h12V3H3z', fill: 'rock' }],
  traverse: [{ d: 'm18 8 4 4-4 4' }, { d: 'M2 12h20' }, { d: 'm6 8-4 4 4 4' }],
  pockets: [ROCK_FACE, hole(8.5, 8.5, 2.2, 1.8), hole(16, 11, 2, 1.6), hole(9.5, 16, 2.4, 1.9)],
  crimps: [ROCK_FACE, { d: 'M7 8h4M13 12.5h4M7 17h4' }],
  endurance: [{ d: 'M6 16c5 0 7-8 12-8a4 4 0 0 1 0 8c-5 0-7-8-12-8a4 4 0 1 0 0 8' }],
  crux: [
    {
      d: 'M4 14a1 1 0 0 1-.78-1.63l9.9-10.2a.5.5 0 0 1 .86.46l-1.92 6.02A1 1 0 0 0 13 10h7a1 1 0 0 1 .78 1.63l-9.9 10.2a.5.5 0 0 1-.86-.46l1.92-6.02A1 1 0 0 0 11 14z',
    },
  ],
  // A jump: the arc a dyno flies, from where it left the wall.
  dyno: [{ d: 'M5 19C6 11.5 11 6.5 19 5' }, { d: 'M14 3.5 19.5 5l-2 5' }, hole(5, 20, 1.5, 1.5)],
};
